import { useState, useMemo, useEffect } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay, addDays, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './AgendaCalendar.css';

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: (date) => startOfWeek(date, { weekStartsOn: 1 }),
  getDay,
  locales: { 'pt-BR': ptBR },
});

const WEEKDAY_MAP = {
  'Domingo': 0, 'Segunda': 1, 'Terça': 2, 'Quarta': 3,
  'Quinta': 4, 'Sexta': 5, 'Sábado': 6,
};

function expandSessions(sessions, sessionTypes, agendaStudents, rangeStart, rangeEnd) {
  const events = [];
  for (const session of sessions) {
    const type = sessionTypes.find(t => t.id === session.typeId);
    const students = agendaStudents.filter(s => session.studentIds.includes(s.id));
    const title = session.title || type?.name || 'Sessão';
    const label = students.length > 0
      ? students.map(s => s.name.split(' ')[0]).join(', ')
      : title;

    const buildEvent = (dateStr) => {
      const [y, m, d] = dateStr.split('-').map(Number);
      const [h, min] = session.time.split(':').map(Number);
      const start = new Date(y, m - 1, d, h, min);
      const end = new Date(start.getTime() + (session.durationMin || 60) * 60000);
      return {
        id: `${session.id}-${dateStr}`,
        sessionId: session.id,
        occurrenceDate: dateStr,
        title: label,
        typeName: title,
        typeColor: type?.color || '#4ade80',
        start,
        end,
        session,
        students,
      };
    };

    if (!session.recurrence?.type) {
      if (session.date >= rangeStart && session.date <= rangeEnd) {
        events.push(buildEvent(session.date));
      }
    } else if (session.recurrence.type === 'weekly') {
      const recEnd = session.recurrence.endDate || rangeEnd;
      const effectiveEnd = recEnd < rangeEnd ? recEnd : rangeEnd;
      let cursor = parseISO(session.date > rangeStart ? session.date : rangeStart);
      const end = parseISO(effectiveEnd);
      while (cursor <= end) {
        const dayIndex = getDay(cursor);
        const dayName = Object.keys(WEEKDAY_MAP).find(k => WEEKDAY_MAP[k] === dayIndex);
        if (session.recurrence.days.includes(dayName)) {
          const dateStr = format(cursor, 'yyyy-MM-dd');
          if (dateStr >= session.date) events.push(buildEvent(dateStr));
        }
        cursor = addDays(cursor, 1);
      }
    }
  }
  return events;
}

function CustomEvent({ event }) {
  const isPast = event.start < new Date();
  return (
    <div style={{ borderLeft: `3px solid ${event.typeColor}` }}
      className="px-1.5 py-0.5 text-xs truncate h-full"
    >
      <span className="font-medium">{event.title}</span>
      {isPast && <span className="ml-1 opacity-40 text-[10px]">✓</span>}
      <div className="opacity-60 text-[10px]">{event.typeName}</div>
    </div>
  );
}

function CustomToolbar({ date, view, onNavigate, onView }) {
  const label = format(date, view === 'week' ? "'Semana de' dd MMM yyyy" : 'MMMM yyyy', { locale: ptBR });
  return (
    <div className="flex items-center justify-between mb-4 px-1">
      <div className="flex items-center gap-2">
        <button onClick={() => onNavigate('PREV')}
          className="p-1.5 border border-white/10 hover:border-white/30 text-[#A3A3A3] hover:text-white transition-colors"
        >
          <ChevronLeft size={14} />
        </button>
        <button onClick={() => onNavigate('TODAY')}
          className="px-3 py-1.5 border border-white/10 hover:border-white/30 text-xs text-[#A3A3A3] hover:text-white uppercase tracking-wider transition-colors"
        >
          Hoje
        </button>
        <button onClick={() => onNavigate('NEXT')}
          className="p-1.5 border border-white/10 hover:border-white/30 text-[#A3A3A3] hover:text-white transition-colors"
        >
          <ChevronRight size={14} />
        </button>
        <span className="text-sm font-medium capitalize ml-2">{label}</span>
      </div>
      <div className="flex gap-1">
        {['week', 'month'].map(v => (
          <button key={v} onClick={() => onView(v)}
            className={`px-3 py-1.5 text-xs uppercase tracking-wider border transition-colors ${
              view === v
                ? 'border-white text-white'
                : 'border-white/10 text-[#A3A3A3] hover:border-white/30'
            }`}
          >
            {v === 'week' ? 'Semana' : 'Mês'}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function AgendaCalendar({
  sessions = [], sessionTypes = [], agendaStudents = [],
  onRangeChange, onSlotSelect, onSessionClick,
}) {
  const [view, setView] = useState('week');
  const [date, setDate] = useState(new Date());

  const rangeStart = useMemo(() => {
    const d = new Date(date);
    if (view === 'week') {
      const day = d.getDay();
      const diff = day === 0 ? -6 : 1 - day;
      d.setDate(d.getDate() + diff);
    } else {
      d.setDate(1);
    }
    d.setHours(0, 0, 0, 0);
    return d;
  }, [date, view]);

  const rangeEnd = useMemo(() => {
    const d = new Date(rangeStart);
    if (view === 'week') d.setDate(d.getDate() + 6);
    else { d.setMonth(d.getMonth() + 1); d.setDate(0); }
    d.setHours(23, 59, 59, 999);
    return d;
  }, [rangeStart, view]);

  useEffect(() => {
    onRangeChange?.({ start: rangeStart, end: rangeEnd });
  }, [rangeStart, rangeEnd, onRangeChange]);

  const events = useMemo(() => {
    const rs = format(rangeStart, 'yyyy-MM-dd');
    const re = format(rangeEnd, 'yyyy-MM-dd');
    return expandSessions(sessions, sessionTypes, agendaStudents, rs, re);
  }, [sessions, sessionTypes, agendaStudents, rangeStart, rangeEnd]);

  const eventStyleGetter = (event) => ({
    style: {
      backgroundColor: event.typeColor + '22',
      border: `1px solid ${event.typeColor}44`,
      borderLeft: `3px solid ${event.typeColor}`,
      borderRadius: '2px',
      color: '#F5F5F5',
      padding: 0,
    },
  });

  const minTime = new Date();
  minTime.setHours(5, 0, 0, 0);
  const maxTime = new Date();
  maxTime.setHours(23, 59, 59, 0);

  return (
    <div className="agenda-calendar-wrap">
      <Calendar
        localizer={localizer}
        events={events}
        view={view}
        date={date}
        onView={setView}
        onNavigate={setDate}
        culture="pt-BR"
        startAccessor="start"
        endAccessor="end"
        style={{ height: 620 }}
        min={minTime}
        max={maxTime}
        scrollToTime={new Date()}
        eventPropGetter={eventStyleGetter}
        components={{
          toolbar: CustomToolbar,
          event: CustomEvent,
        }}
        selectable
        onSelectSlot={(slot) => onSlotSelect?.(slot.start)}
        onSelectEvent={(event) => onSessionClick?.(event.session, event.occurrenceDate)}
        messages={{
          week: 'Semana', month: 'Mês', day: 'Dia', today: 'Hoje',
          previous: 'Anterior', next: 'Próximo',
          showMore: (total) => `+${total} mais`,
          noEventsInRange: 'Nenhuma sessão neste período.',
        }}
      />
    </div>
  );
}
